/**
 * Process-wide cache of signed media URLs, keyed by `blob_path`.
 *
 * Chat attachments live in a private container, so `data_url` is an identity
 * rather than a link. `GET /api/v1/uploads/media-url` re-checks room access and
 * mints a read-only SAS good for ~15 minutes; that URL goes straight onto an
 * `<img>`/`<video>`/`<iframe>` src, which browsers load without any CORS check.
 *
 * We cache keyed on the stable blob_path and honour `expires_at`, refreshing a
 * little early so a URL never goes stale mid-render. A module singleton, like
 * roomMessagesCache — the same photo stays resolved as the coach scrolls a
 * thread, switches rooms and comes back.
 */
import { get } from "../../service/comms/commsApi";
import { getMediaUrl } from "../../service/comms/messagesService";

/** Treat a URL as stale this long before it actually expires. */
const EXPIRY_MARGIN_MS = 60 * 1000;

/** Assumed lifetime when the API omits expires_at. */
const DEFAULT_TTL_MS = 15 * 60 * 1000;

/** Cached URLs retained before the least-recently-used ones are dropped. */
const MAX_ENTRIES = 200;

/** @type {Map<string, { url: string, expiresAt: number, promise: Promise<string>|null, touched: number }>} */
const cache = new Map();
let clock = 0;

/**
 * Parse the API's expiry into an epoch-ms deadline, minus a safety margin.
 * @param {string|undefined} expiresAt
 * @returns {number}
 */
const deadlineFrom = (expiresAt) => {
  const parsed = expiresAt ? Date.parse(expiresAt) : NaN;
  const expiry = Number.isFinite(parsed) ? parsed : Date.now() + DEFAULT_TTL_MS;
  return expiry - EXPIRY_MARGIN_MS;
};

/** Drop least-recently-used entries once the map grows past the cap. */
const evictOverflow = () => {
  if (cache.size <= MAX_ENTRIES) return;
  [...cache.entries()]
    .filter(([, entry]) => entry.url)
    .sort((a, b) => a[1].touched - b[1].touched)
    .slice(0, cache.size - MAX_ENTRIES)
    .forEach(([key]) => cache.delete(key));
};

/**
 * A cached signed URL that is still fresh, without triggering a request.
 * Marks the entry as recently used.
 *
 * @param {string} blobPath
 * @returns {string|null}
 */
export function peekMediaUrl(blobPath) {
  const entry = blobPath ? cache.get(blobPath) : null;
  if (!entry?.url) return null;
  if (Date.now() >= entry.expiresAt) return null;
  entry.touched = ++clock;
  return entry.url;
}

/**
 * Resolve a blob path to a signed URL, fetching once per expiry window.
 * Concurrent callers for the same path share a single request.
 *
 * @param {string} blobPath
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Bypass the cached URL (e.g. after a
 *   media element reported a load error on an expired SAS).
 * @returns {Promise<string>}
 */
export function loadMediaUrl(blobPath, { force = false } = {}) {
  if (!blobPath) return Promise.reject(new Error("Missing blob path"));

  if (force) cache.delete(blobPath);

  const existing = cache.get(blobPath);
  if (existing?.url && Date.now() < existing.expiresAt) {
    existing.touched = ++clock;
    return Promise.resolve(existing.url);
  }
  if (existing?.promise) return existing.promise;

  const promise = getMediaUrl(get, blobPath)
    .then((data) => {
      const url = data?.url;
      if (!url) throw new Error("Attachment could not be loaded.");
      cache.set(blobPath, {
        url,
        expiresAt: deadlineFrom(data.expires_at),
        promise: null,
        touched: ++clock,
      });
      evictOverflow();
      return url;
    })
    .catch((err) => {
      // Don't cache failures — a 403 can become valid once membership syncs.
      cache.delete(blobPath);
      throw err;
    });

  cache.set(blobPath, { url: "", expiresAt: 0, promise, touched: ++clock });
  return promise;
}

/**
 * Forget every signed URL. Called on logout — these are scoped credentials and
 * must not carry into the next account.
 */
export function clearMediaCache() {
  cache.clear();
}
